/**
 * The ripple.
 *
 * Touch feedback on every pressable thing: the ink spreads from where the finger or pointer
 * actually landed, which is what makes it read as a response to *you* rather than an animation
 * that happens to be playing.
 *
 * **One listener on the document, not a wrapper component.** Every button in this app is a plain
 * `<button>`, so a delegated `pointerdown` catches all of them — including the ones added
 * tomorrow, by someone who has never read this file. A `<Ripple>` component would have to be
 * remembered thirty times and would be forgotten on the thirty-first, and wrapping every control
 * would mean touching every screen to add a visual flourish.
 *
 * **Links that look like controls ripple; links that are text do not.** A navigation item and a
 * link styled as a button are pressed, and the sidebar felt dead without it. A client's name in a
 * table cell is read, and rippling it on the way to another page is noise — so `a.nav-item`,
 * `a.btn` and `a.btn-sm` are in, bare `<a>` is out, and anything can opt in with `data-ripple`.
 *
 * The wave is absolutely positioned inside the target, so the target needs `position: relative`
 * and `overflow: hidden` for the circle to be clipped to its shape. Both are applied here rather
 * than in the stylesheet, because a button that has neither would otherwise leak a growing
 * circle across the page — and only the elements this code actually touches need them.
 */

const SELECTOR = [
  "button",
  "[role='button']",
  "a.nav-item",
  "a.btn",
  "a.btn-sm",
  "[data-ripple]",
].join(", ");

/** Elements that should never ripple, even though they match. */
function skip(element: HTMLElement): boolean {
  if (element.hasAttribute("data-no-ripple")) return true;
  if (element.hasAttribute("disabled")) return true;
  if (element.getAttribute("aria-disabled") === "true") return true;
  return false;
}

/**
 * Above this, a press is not a press.
 *
 * A full-screen invisible element — the backdrop that closes an open menu when you click away —
 * matches `button` and would spread ink across the entire page. Anything approaching the size of
 * the viewport is such an element, never a control somebody aimed at.
 */
function tooBigToBeAControl(rect: DOMRect): boolean {
  return rect.width >= window.innerWidth * 0.9 && rect.height >= window.innerHeight * 0.9;
}

function spawn(host: HTMLElement, clientX: number, clientY: number) {
  const rect = host.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  if (tooBigToBeAControl(rect)) return;

  // The circle must reach the far corner, or a press near one edge leaves the opposite corner
  // untouched and the fill looks broken rather than deliberate.
  const dx = Math.max(clientX - rect.left, rect.right - clientX);
  const dy = Math.max(clientY - rect.top, rect.bottom - clientY);
  const radius = Math.hypot(dx, dy);

  const computed = getComputedStyle(host);
  if (computed.position === "static") host.style.position = "relative";
  if (computed.overflow === "visible") host.style.overflow = "hidden";

  const wave = document.createElement("span");
  wave.className = "ripple-wave";
  wave.style.width = `${radius * 2}px`;
  wave.style.height = `${radius * 2}px`;
  wave.style.left = `${clientX - rect.left - radius}px`;
  wave.style.top = `${clientY - rect.top - radius}px`;
  wave.addEventListener("animationend", () => wave.remove(), { once: true });
  host.appendChild(wave);

  // Belt and braces: if the animation never fires — a background tab, a cancelled transition —
  // the node must still go, or a long session accumulates thousands of them.
  window.setTimeout(() => wave.remove(), 1200);
}

/**
 * Attach the listener. Idempotent, so a hot reload cannot end up with two.
 *
 * Returns nothing to unsubscribe with on purpose: this lives for the life of the document, and
 * an unsubscribe nobody calls is just a thing to get wrong.
 */
export function installRipple(): void {
  const doc = document as Document & { __rentorngRipple?: boolean };
  if (doc.__rentorngRipple) return;
  doc.__rentorngRipple = true;

  // Someone who has asked the operating system for less motion gets none.
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

  document.addEventListener(
    "pointerdown",
    (event) => {
      // Only a primary press. A right-click or a middle-click is not a press of this button.
      if (event.button !== 0) return;
      const target = event.target as HTMLElement | null;
      const host = target?.closest<HTMLElement>(SELECTOR);
      if (!host || skip(host)) return;
      spawn(host, event.clientX, event.clientY);
    },
    // Passive: this never calls preventDefault, and saying so keeps scrolling smooth on touch.
    { passive: true },
  );
}
